import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Calendar, ShoppingBag, LogOut, Leaf, Shield, ArrowRight, FileText, Image as ImageIcon, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    if (!loading && !user) nav('/', { replace: true });
  }, [user, loading, nav]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [b, o, mp] = await Promise.all([
          axios.get(`${API}/bookings/mine`, { withCredentials: true }),
          axios.get(`${API}/orders/mine`, { withCredentials: true }),
          axios.get(`${API}/meal-plans/mine`, { withCredentials: true }),
        ]);
        setBookings(b.data);
        setOrders(o.data);
        setPlans(mp.data);
      } catch { /* ignore */ }
    })();
  }, [user]);

  if (loading || !user) return <div className="min-h-screen bg-[#faf7f1] flex items-center justify-center"><div className="text-[#7a8471]">Loading…</div></div>;

  return (
    <div className="min-h-screen bg-[#faf7f1]">
      <header className="bg-white border-b border-neutral-100 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src="https://customer-assets-jt897jd0.emergentagent.net/job_prachi-premium/artifacts/92rpbx3r_nutrify_me_logo.png" alt="Nutrify Me" className="w-9 h-9 object-contain" />
            <div className="font-serif text-lg text-[#1a2b17]">Nutrify Me</div>
          </a>
          <div className="flex items-center gap-3">
            {user.is_admin && (
              <Button onClick={() => nav('/admin')} variant="outline" className="rounded-full h-9 gap-1">
                <Shield className="w-4 h-4" /> Admin
              </Button>
            )}
            {user.picture && <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full" />}
            <Button variant="outline" onClick={logout} className="rounded-full h-9"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-10">
          <div className="text-xs tracking-[0.2em] uppercase text-[#c9743a] mb-2">Welcome back</div>
          <h1 className="font-serif text-4xl md:text-5xl text-[#1a2b17]">Hi {user.name?.split(' ')[0]}</h1>
          <p className="text-[#4a5544] mt-2">Here’s everything happening on your journey.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <section className="bg-white rounded-3xl p-8 shadow-sm">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-2xl text-[#1a2b17] flex items-center gap-2"><Calendar className="w-5 h-5 text-[#3a5a2c]" /> Consultations</h2>
              <Button onClick={() => nav('/')} className="rounded-full h-9 bg-[#3a5a2c] hover:bg-[#2d4622] text-white">Book new</Button>
            </div>
            {bookings.length === 0 && <p className="text-sm text-[#7a8471]">No consultations yet. Book your first free call.</p>}
            <ul className="space-y-3">
              {bookings.map((b) => (
                <li key={b.id} className="p-4 rounded-2xl bg-[#faf7f1] border border-neutral-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-[#1a2b17]">{b.goal || 'Free consultation'}</div>
                      <div className="text-xs text-[#7a8471]">{new Date(b.created_at).toLocaleString()}</div>
                    </div>
                    <span className="text-[10px] tracking-widest uppercase bg-[#fce7d5] text-[#c9743a] px-2 py-1 rounded-full">{b.status}</span>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className="bg-white rounded-3xl p-8 shadow-sm">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-2xl text-[#1a2b17] flex items-center gap-2"><ShoppingBag className="w-5 h-5 text-[#3a5a2c]" /> Orders</h2>
              <Button onClick={() => nav('/')} className="rounded-full h-9 bg-[#3a5a2c] hover:bg-[#2d4622] text-white">Shop</Button>
            </div>
            {orders.length === 0 && <p className="text-sm text-[#7a8471]">No orders yet.</p>}
            <ul className="space-y-3">
              {orders.map((o) => (
                <li key={o.id} className="p-4 rounded-2xl bg-[#faf7f1] border border-neutral-100">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-mono text-xs text-[#7a8471]">{o.id}</div>
                    <div className="text-sm font-medium">₹{o.total}</div>
                  </div>
                  <div className="text-xs text-[#4a5544]">{o.items.length} item(s) · {new Date(o.created_at).toLocaleDateString()}</div>
                </li>
              ))}
            </ul>
          </section>

          <section className="bg-gradient-to-br from-[#3a5a2c] to-[#4d6f3d] text-white rounded-3xl p-8 md:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-3xl flex items-center gap-2"><FileText className="w-6 h-6" /> Your meal plans</h2>
              <span className="text-xs tracking-widest uppercase bg-white/10 px-3 py-1 rounded-full">{plans.length} uploads</span>
            </div>
            {plans.length === 0 ? (
              <>
                <p className="text-white/80 mb-5">Your dietitian will publish weekly meal plans, macros and check-ins here.</p>
                <Button onClick={() => nav('/quiz')} className="rounded-full bg-white text-[#1a2b17] hover:bg-[#f4d5a8]">Take diet quiz <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {plans.map((p) => (
                  <a key={p.id} href={`${BASE}${p.file_url}`} target="_blank" rel="noopener noreferrer" className="group bg-white/10 hover:bg-white/20 border border-white/15 rounded-2xl p-4 flex items-center gap-4 transition">
                    <div className="w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center flex-shrink-0">
                      {p.kind === 'pdf' ? <FileText className="w-5 h-5" /> : <ImageIcon className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{p.title}</div>
                      <div className="text-xs text-white/70 truncate">{p.week_of || new Date(p.created_at).toLocaleDateString()}</div>
                      {p.notes && <div className="text-xs text-white/60 mt-1 line-clamp-1">{p.notes}</div>}
                    </div>
                    <Download className="w-4 h-4 opacity-70 group-hover:opacity-100" />
                  </a>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
